if (!customElements.get('product-bundle-builder')) {
  class ProductBundleBuilder extends HTMLElement {
    constructor() {
      super();

      // 配置
      this.config = {
        productId: this.dataset.productId,
        sectionId: this.dataset.sectionId,
        discountPercentage: parseFloat(this.dataset.discountPercentage) || 0,
        moneyFormat: this.dataset.moneyFormat || window.theme?.settings?.money_with_currency_format || Shopify?.money_format || '{{amount}}',
        productsPerPage: {
          desktop: parseInt(this.dataset.productsPerPageDesktop) || 12,
          tablet: parseInt(this.dataset.productsPerPageTablet) || 9,
          mobile: parseInt(this.dataset.productsPerPageMobile) || 6
        }
      };

      // 状态管理
      this.state = {
        mainProduct: {
          variantId: null,
          quantity: 1,
          price: 0,
          compareAtPrice: 0,
          available: true
        },
        collections: {
          collection_1: {
            selectedProducts: new Map(),
            currentPage: 1
          },
          collection_2: {
            selectedProducts: new Map(),
            currentPage: 1
          },
          collection_3: {
            selectedProducts: new Map(),
            currentPage: 1
          }
        }
      };

      // DOM 引用
      this.elements = {
        accordion: this.querySelector('.bundle-accordion'),
        mainVariantSelector: this.querySelector('[data-main-product-variant]'),
        mainQuantityInput: this.querySelector('[data-main-product-quantity] input[type="number"]'),
        mainPriceDisplay: this.querySelector('[data-main-product-price]'),
        addToCartButton: this.querySelector('[data-bundle-add-to-cart]'),
        categoryCards: this.querySelectorAll('.bundle-category-card')
        // Note: modals 不在这里缓存，因为它们会被 modal-dialog.js 移动到 body
      };
    }

    connectedCallback() {
      this.initializeMainProduct();
      this.setupEventListeners();
      this.updateUI();

      // 标记初始化完成 - 之后的价格更新才会生效
      this._initialized = true;

      // Debug: 检查modal和modal-opener是否正确加载
      console.log('[Bundle Builder] Initialized');
      console.log('[Bundle Builder] Modal dialogs found:', this.querySelectorAll('modal-dialog').length);
      console.log('[Bundle Builder] Modal openers found:', this.querySelectorAll('modal-opener').length);

      // 检查modal-opener是否有正确的data-modal属性
      this.querySelectorAll('modal-opener').forEach((opener, index) => {
        const modalId = opener.getAttribute('data-modal');
        const modal = document.querySelector(modalId);
        console.log(`[Bundle Builder] Opener ${index + 1}: data-modal="${modalId}", modal found:`, !!modal);

        // 检查按钮点击事件
        const button = opener.querySelector('button');
        if (button) {
          console.log(`[Bundle Builder] Opener ${index + 1}: button found, adding test listener`);
          button.addEventListener('click', () => {
            console.log(`[Bundle Builder] Button clicked for modal: ${modalId}`);
          });
        } else {
          console.warn(`[Bundle Builder] Opener ${index + 1}: NO BUTTON FOUND!`);
        }
      });

      // 检查modal-dialog custom element是否已定义
      console.log('[Bundle Builder] modal-dialog defined:', !!customElements.get('modal-dialog'));
      console.log('[Bundle Builder] modal-opener defined:', !!customElements.get('modal-opener'));
    }

    /**
     * 初始化主产品
     * 支持有变体和无变体两种情况的降级策略
     */
    initializeMainProduct() {
      if (this.elements.mainVariantSelector) {
        // 有变体选择器：从选择器的选项中获取价格信息
        const selectedOption = this.elements.mainVariantSelector.selectedOptions[0];
        this.state.mainProduct = {
          variantId: parseInt(selectedOption.value),
          quantity: 1,
          price: parseFloat(selectedOption.dataset.price) / 100,
          compareAtPrice: parseFloat(selectedOption.dataset.comparePrice || 0) / 100,
          available: selectedOption.dataset.available === 'true'
        };
        console.log('[Bundle Builder] Main product initialized from variant selector:', this.state.mainProduct);
      } else {
        // 无变体选择器（单变体产品）：从主产品容器的data属性获取
        const mainProductEl = this.querySelector('.bundle-main-product');
        if (mainProductEl) {
          this.state.mainProduct = {
            variantId: parseInt(mainProductEl.dataset.mainVariantId) || null,
            quantity: 1,
            price: parseFloat(mainProductEl.dataset.mainPrice || 0) / 100,
            compareAtPrice: parseFloat(mainProductEl.dataset.mainComparePrice || 0) / 100,
            available: mainProductEl.dataset.mainAvailable === 'true'
          };
          console.log('[Bundle Builder] Main product initialized from data attributes (no variant selector):', this.state.mainProduct);
        } else {
          console.warn('[Bundle Builder] Could not find main product element for initialization');
        }
      }
    }

    /**
     * 设置事件监听
     */
    setupEventListeners() {
      // 🔧 防止重复注册事件监听器（当组件被移动时 connectedCallback 会再次触发）
      if (this._eventListenersSetup) {
        console.log('[Bundle Builder] Event listeners already set up, skipping duplicate registration');
        return;
      }
      this._eventListenersSetup = true;
      console.log('[Bundle Builder] Setting up event listeners for the first time');

      // 主产品变体选择
      if (this.elements.mainVariantSelector) {
        this.elements.mainVariantSelector.addEventListener('change', (e) => {
          this.handleMainVariantChange(e);
        });
      }

      // 主产品数量选择
      if (this.elements.mainQuantityInput) {
        const quantityContainer = this.querySelector('[data-main-product-quantity]');

        // 更新按钮状态的辅助函数
        const updateButtonStates = () => {
          const currentValue = parseInt(this.elements.mainQuantityInput.value) || 1;
          const minusBtn = quantityContainer?.querySelector('.minus');
          if (minusBtn) {
            minusBtn.disabled = currentValue <= 1;
          }
        };

        // 监听 input 的 change 事件
        this.elements.mainQuantityInput.addEventListener('change', (e) => {
          const value = parseInt(e.target.value) || 1;
          this.state.mainProduct.quantity = Math.max(1, value);
          this.elements.mainQuantityInput.value = this.state.mainProduct.quantity;
          updateButtonStates();
          this.updateCartBadges();
          this.updateAddToCartButton();
        });

        // 监听 +/- 按钮点击
        if (quantityContainer) {
          const plusBtn = quantityContainer.querySelector('.plus');
          const minusBtn = quantityContainer.querySelector('.minus');

          if (plusBtn) {
            plusBtn.addEventListener('click', () => {
              const currentValue = parseInt(this.elements.mainQuantityInput.value) || 1;
              this.elements.mainQuantityInput.value = currentValue + 1;
              this.state.mainProduct.quantity = currentValue + 1;
              updateButtonStates();
              this.updateCartBadges();
              this.updateAddToCartButton();
            });
          }

          if (minusBtn) {
            minusBtn.addEventListener('click', () => {
              const currentValue = parseInt(this.elements.mainQuantityInput.value) || 1;
              if (currentValue > 1) {
                this.elements.mainQuantityInput.value = currentValue - 1;
                this.state.mainProduct.quantity = currentValue - 1;
                updateButtonStates();
                this.updateCartBadges();
                this.updateAddToCartButton();
              }
            });
          }

          // 初始化按钮状态
          updateButtonStates();
        }
      }

      // 类目卡片事件
      this.elements.categoryCards.forEach(card => {
        this.setupCategoryCardEvents(card);
      });

      // 弹窗事件 - 使用 setTimeout 确保 modal-dialog.js 已经处理完毕
      setTimeout(() => {
        this.setupAllModalEvents();
      }, 100);

      // Add Bundle to Cart 按钮
      this.elements.addToCartButton.addEventListener('click', () => {
        this.handleAddBundleToCart();
      });

      // 产品卡片事件（委托） - 使用document因为modal会被移动到body
      document.addEventListener('click', (e) => {
        // 检查是否在当前bundle builder的modal内
        const modal = e.target.closest('.bundle-modal');
        if (!modal) return;

        // 检查modal是否属于当前bundle builder
        const modalCategoryId = modal.dataset.categoryId;
        if (!this.state.collections[modalCategoryId]) return;

        // Add+ 按钮
        if (e.target.closest('[data-add-to-bundle]')) {
          e.preventDefault();
          const productCard = e.target.closest('.bundle-product-card');
          console.log('[Bundle Builder] Add button clicked', productCard);
          this.handleProductAdd(productCard);
        }

        // 删除按钮
        if (e.target.closest('[data-remove-product]')) {
          e.preventDefault();
          const productCard = e.target.closest('.bundle-product-card');
          console.log('[Bundle Builder] Remove button clicked', productCard);
          this.handleProductRemove(productCard);
        }

        // Modal清空按钮
        if (e.target.closest('[data-clear-all]')) {
          e.preventDefault();
          console.log('[Bundle Builder] Clear all clicked in modal:', modalCategoryId);
          this.handleModalClearAll(modalCategoryId);
        }
      });

      // 删除全部按钮 - 这个在主区域，使用this监听
      this.addEventListener('click', (e) => {
        // 删除全部按钮
        if (e.target.closest('[data-remove-all]')) {
          e.preventDefault();
          const card = e.target.closest('.bundle-category-card');
          console.log('[Bundle Builder] Remove all clicked', card.dataset.categoryId);
          this.removeAllProducts(card.dataset.categoryId);
        }
      });

      // 产品卡片变体选择（委托） - 也使用document
      document.addEventListener('change', (e) => {
        // 检查是否是modal内的变体选择器
        if (e.target.matches('[data-variant-selector]')) {
          const modal = e.target.closest('.bundle-modal');
          if (modal && this.state.collections[modal.dataset.categoryId]) {
            console.log('[Bundle Builder] Variant changed in modal');
            this.handleProductVariantChange(e.target);
          }
        }
      });

      // 类目卡片数量选择（在主区域）
      this.addEventListener('change', (e) => {
        // 外部类目卡片数量选择
        if (e.target.matches('[data-quantity-input]')) {
          const card = e.target.closest('.bundle-category-card');
          this.handleCategoryQuantityChange(card.dataset.categoryId, parseInt(e.target.value));
        }
      });

      // 管理按钮点击事件
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-manage-products]')) {
          e.preventDefault();
          e.stopPropagation(); // 防止触发modal-opener
          const card = e.target.closest('.bundle-category-card');
          const categoryId = card.dataset.categoryId;
          console.log('[Bundle Builder] Manage button clicked for:', categoryId);
          this.openManageModal(categoryId);
        }
      });

      // 管理弹窗事件（使用document因为modal可能被移动）
      document.addEventListener('click', (e) => {
        // 关闭按钮
        if (e.target.closest('[data-manage-close]')) {
          const modal = e.target.closest('.bundle-manage-modal');
          const categoryId = modal.dataset.categoryId;
          this.handleManageClose(categoryId);
        }

        // Confirm按钮
        if (e.target.closest('[data-manage-confirm]')) {
          const modal = e.target.closest('.bundle-manage-modal');
          const categoryId = modal.dataset.categoryId;
          this.handleManageConfirm(categoryId);
        }

        // 背景遮罩点击
        if (e.target.matches('[data-manage-backdrop]')) {
          const modal = e.target.closest('.bundle-manage-modal');
          const categoryId = modal.dataset.categoryId;
          this.handleManageClose(categoryId);
        }

        // 数量调整按钮
        const manageModal = e.target.closest('.bundle-manage-modal');
        if (manageModal) {
          const categoryId = manageModal.dataset.categoryId;

          if (e.target.closest('.bundle-manage-product-item__quantity .plus')) {
            const item = e.target.closest('.bundle-manage-product-item');
            const variantId = parseInt(item.dataset.variantId);
            this.handleManageQuantityChange(categoryId, variantId, 1);
          }

          if (e.target.closest('.bundle-manage-product-item__quantity .minus')) {
            const item = e.target.closest('.bundle-manage-product-item');
            const variantId = parseInt(item.dataset.variantId);
            this.handleManageQuantityChange(categoryId, variantId, -1);
          }

          // 删除按钮
          if (e.target.closest('[data-manage-remove]')) {
            const item = e.target.closest('.bundle-manage-product-item');
            const variantId = parseInt(item.dataset.variantId);
            this.handleManageProductRemove(categoryId, variantId);
          }
        }
      });

      // 数量输入框change事件
      document.addEventListener('change', (e) => {
        if (e.target.matches('.bundle-manage-product-item__quantity input')) {
          const item = e.target.closest('.bundle-manage-product-item');
          const modal = item.closest('.bundle-manage-modal');
          const categoryId = modal.dataset.categoryId;
          const variantId = parseInt(item.dataset.variantId);
          const newQuantity = parseInt(e.target.value) || 1;

          const product = this.state.collections[categoryId].selectedProducts.get(variantId);
          if (product) {
            product.quantity = Math.max(1, newQuantity);
            modal.dataset.modified = 'true';
            this.updateManageModalContent(categoryId);
          }
        }

        // 🔧 管理弹窗中的变体选择器change事件
        if (e.target.matches('[data-manage-variant-selector]')) {
          const select = e.target;
          const item = select.closest('.bundle-manage-product-item');
          const modal = item.closest('.bundle-manage-modal');
          const categoryId = modal.dataset.categoryId;
          const oldVariantId = parseInt(item.dataset.variantId);
          const selectedOption = select.selectedOptions[0];
          const newVariantId = parseInt(selectedOption.value);

          console.log('[Bundle Builder] Manage modal variant change:', {
            categoryId,
            oldVariantId,
            newVariantId
          });

          // 调用变体切换处理方法
          this.handleManageVariantChange(categoryId, oldVariantId, newVariantId, selectedOption);
        }
      });

      // ESC键关闭管理弹窗
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const openManageModal = document.querySelector('.bundle-manage-modal[open]');
          if (openManageModal) {
            const categoryId = openManageModal.dataset.categoryId;
            this.handleManageClose(categoryId);
          }
        }
      });
    }

    /**
     * 设置类目卡片事件
     */
    setupCategoryCardEvents(card) {
      const categoryId = card.dataset.categoryId;

      // 数量选择器 - 监听主题的 quantity-selector 触发的 change 事件
      const quantityInput = card.querySelector('[data-quantity-selector] .qty');
      if (quantityInput) {
        quantityInput.addEventListener('change', (e) => {
          const value = parseInt(e.target.value) || 1;
          this.handleCategoryQuantityChange(categoryId, Math.max(1, value));
        });
      }
    }

    /**
     * 设置所有弹窗事件
     */
    setupAllModalEvents() {
      // 使用 document 查找所有 modal，因为它们已经被移动到 body
      const allModals = document.querySelectorAll('.bundle-modal');

      allModals.forEach(modal => {
        const categoryId = modal.dataset.categoryId;
        // 只处理属于当前 bundle builder 的 modal
        if (this.state.collections[categoryId]) {
          console.log('[Bundle Builder] Setting up modal events for:', categoryId);
          this.setupModalEvents(modal);
        }
      });
    }

    /**
     * 设置弹窗事件
     */
    setupModalEvents(modal) {
      const categoryId = modal.dataset.categoryId;

      // Learn More按钮
      const learnMoreBtn = modal.querySelector('[data-learn-more]');
      if (learnMoreBtn) {
        const grid = modal.querySelector('[data-products-grid]');
        const hiddenCards = grid.querySelectorAll('[data-initially-hidden="true"]');

        // 如果有隐藏的产品，显示按钮
        if (hiddenCards.length > 0) {
          learnMoreBtn.style.display = 'block';
        }

        learnMoreBtn.addEventListener('click', () => {
          console.log('[Bundle Builder] Learn More clicked, showing', hiddenCards.length, 'products');

          // 显示所有隐藏的产品
          hiddenCards.forEach(card => {
            card.style.display = '';
            card.classList.remove('bundle-product-card--hidden');
          });

          // 隐藏按钮
          learnMoreBtn.style.display = 'none';
        });
      }

      // Review Bundle 按钮 - 使用事件委托
      const reviewBtn = modal.querySelector('[data-review-bundle]');
      if (reviewBtn) {
        reviewBtn.addEventListener('click', (e) => {
          console.log('[Bundle Builder] Review button clicked');
          this.closeModal(categoryId);
        });
      }

      // Close 按钮
      const closeBtn = modal.querySelector('.bundle-modal__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          this.closeModal(categoryId);
        });
      }
    }

    /**
     * 主产品变体变化
     */
    handleMainVariantChange(e) {
      const selectedOption = e.target.selectedOptions[0];
      this.state.mainProduct.variantId = parseInt(selectedOption.value);
      this.state.mainProduct.price = parseFloat(selectedOption.dataset.price) / 100;
      this.state.mainProduct.compareAtPrice = parseFloat(selectedOption.dataset.comparePrice || 0) / 100;
      this.state.mainProduct.available = selectedOption.dataset.available === 'true';

      // 更新主产品图片
      const mainImageEl = this.querySelector('.bundle-main-product .bundle-product-image img');
      if (mainImageEl && selectedOption.dataset.image) {
        mainImageEl.src = selectedOption.dataset.image;
      }

      this.updateMainProductPrice();
      this.updateAddToCartButton();
    }

    /**
     * 主产品数量变化
     */
    handleMainQuantityChange(e) {
      const quantity = parseInt(e.target.value) || 1;
      this.state.mainProduct.quantity = Math.max(1, quantity);
      this.updateCartBadges();
      this.updateAddToCartButton();
    }

    /**
     * 更新主产品价格显示
     * 注意：只在变体切换时调用，初始化时不调用（保留Liquid渲染的价格，兼容Transcy等货币转换应用）
     */
    updateMainProductPrice() {
      // 如果是初始化阶段，不更新价格显示（保留Liquid渲染的原始内容）
      if (!this._initialized) return;

      const priceDisplay = this.elements.mainPriceDisplay;
      if (!priceDisplay) return;

      const comparePriceEl = priceDisplay.querySelector('.compare-price');
      const currentPriceEl = priceDisplay.querySelector('.current-price');

      // Update current price - 使用 innerHTML 以支持 Transcy HTML
      if (currentPriceEl) {
        currentPriceEl.innerHTML = this.formatMoney(this.state.mainProduct.price);
      }

      // Update compare price
      if (this.state.mainProduct.compareAtPrice > this.state.mainProduct.price) {
        // Show compare price - 使用 innerHTML 以支持 Transcy HTML
        if (comparePriceEl) {
          comparePriceEl.innerHTML = this.formatMoney(this.state.mainProduct.compareAtPrice);
          comparePriceEl.style.display = '';
        }
      } else {
        // Hide compare price
        if (comparePriceEl) {
          comparePriceEl.style.display = 'none';
        }
      }
    }

    /**
     * 产品添加到Bundle
     */
    handleProductAdd(productCard) {
      console.log('[Bundle Builder] handleProductAdd called');
      console.log('[Bundle Builder] Product card:', productCard);
      console.log('[Bundle Builder] Product card dataset:', productCard.dataset);

      // 如果有变体选择器，使用选择器的值而不是 data-variant-id
      const variantSelector = productCard.querySelector('[data-variant-selector]');
      let variantId;

      if (variantSelector) {
        const selectedOption = variantSelector.selectedOptions[0];
        variantId = parseInt(selectedOption.value);
        console.log('[Bundle Builder] Got variant ID from selector:', variantId);

        // 同步更新产品卡片的 data-variant-id
        productCard.dataset.variantId = variantId;
        productCard.dataset.price = selectedOption.dataset.price;
        productCard.dataset.comparePrice = selectedOption.dataset.comparePrice || '';
      } else {
        variantId = parseInt(productCard.dataset.variantId);
        console.log('[Bundle Builder] Got variant ID from data attribute:', variantId);
      }

      // 验证 variantId
      if (!variantId || isNaN(variantId)) {
        console.error('[Bundle Builder] Invalid variant ID:', variantId);
        alert('Error: Cannot find variant. Please refresh and try again.');
        return;
      }

      const categoryId = productCard.closest('.bundle-modal').dataset.categoryId;
      const available = productCard.dataset.available === 'true';

      console.log('[Bundle Builder] Variant ID:', variantId);
      console.log('[Bundle Builder] Category ID:', categoryId);
      console.log('[Bundle Builder] Available:', available);

      // 检查库存
      if (!available) {
        console.warn('[Bundle Builder] Product is out of stock');
        alert('This product is currently out of stock');
        return;
      }

      const selectedProducts = this.state.collections[categoryId].selectedProducts;
      console.log('[Bundle Builder] Current selected products:', selectedProducts);

      if (selectedProducts.has(variantId)) {
        // 数量+1
        const product = selectedProducts.get(variantId);
        product.quantity += 1;
        console.log('[Bundle Builder] Increased quantity to:', product.quantity);
      } else {
        // 新增产品
        const newProduct = {
          productId: parseInt(productCard.dataset.productId),
          productHandle: productCard.dataset.productHandle, // 🔧 存储产品handle，用于获取所有变体
          variantId: variantId,
          quantity: 1,
          price: parseFloat(productCard.dataset.price) / 100,
          compareAtPrice: parseFloat(productCard.dataset.comparePrice || 0) / 100,
          title: productCard.dataset.title,
          image: productCard.dataset.image,
          available: available
        };

        // 🔧 提取产品的所有变体信息（如果有变体选择器）
        const variantSelector = productCard.querySelector('[data-variant-selector]');
        if (variantSelector) {
          const variants = [];
          Array.from(variantSelector.options).forEach(option => {
            variants.push({
              id: parseInt(option.value),
              title: option.text,
              price: parseFloat(option.dataset.price) / 100,
              compareAtPrice: parseFloat(option.dataset.comparePrice || 0) / 100,
              image: option.dataset.image || newProduct.image
            });
          });
          newProduct.variants = variants;
          console.log('[Bundle Builder] Extracted variants:', variants);
        }

        // 验证产品数据
        if (!newProduct.productId || !newProduct.variantId || !newProduct.price) {
          console.error('[Bundle Builder] Invalid product data:', newProduct);
          alert('Error: Invalid product data. Please refresh and try again.');
          return;
        }

        selectedProducts.set(variantId, newProduct);
        console.log('[Bundle Builder] Added new product:', newProduct);
      }

      // 更新UI
      this.updateProductCardState(productCard, categoryId);
      this.updateCategoryCard(categoryId);
      this.updateCartBadges();
      this.updateAddToCartButton();
      console.log('[Bundle Builder] UI updated');

      // 🔧 Campaign模式: dispatch事件通知extension更新display状态
      if (this.dataset.campaignMode === 'true') {
        console.log('[Bundle Builder] Campaign mode detected, dispatching state change event');
        this.dispatchEvent(new CustomEvent('bundle:state-changed', {
          bubbles: true,
          detail: { categoryId, variantId, action: 'add' }
        }));
      }
    }

    /**
     * 产品从Bundle移除
     */
    handleProductRemove(productCard) {
      const variantId = parseInt(productCard.dataset.variantId);
      const categoryId = productCard.closest('.bundle-modal').dataset.categoryId;

      // 直接清零（移除）
      this.state.collections[categoryId].selectedProducts.delete(variantId);

      // 更新UI
      this.updateProductCardState(productCard, categoryId);
      this.updateCategoryCard(categoryId);
      this.updateCartBadges();
      this.updateAddToCartButton();

      // 🔧 Campaign模式: dispatch事件通知extension更新display状态
      if (this.dataset.campaignMode === 'true') {
        console.log('[Bundle Builder] Campaign mode detected, dispatching state change event');
        this.dispatchEvent(new CustomEvent('bundle:state-changed', {
          bubbles: true,
          detail: { categoryId, variantId, action: 'remove' }
        }));
      }
    }

    /**
     * 移除类目的所有产品
     */
    removeAllProducts(categoryId) {
      this.state.collections[categoryId].selectedProducts.clear();

      // 更新弹窗内所有产品卡片状态
      // modal 被 modal-dialog.js 移动到了 body 下，所以使用 document.querySelector
      const modal = document.querySelector(`#BundleModal-${categoryId}`);
      if (modal) {
        modal.querySelectorAll('.bundle-product-card').forEach(card => {
          this.updateProductCardState(card, categoryId);
        });
      }

      this.updateCategoryCard(categoryId);
      this.updateCartBadges();
      this.updateAddToCartButton();
    }

    /**
     * Modal清空按钮处理
     */
    handleModalClearAll(categoryId) {
      // 复用removeAllProducts逻辑
      this.removeAllProducts(categoryId);

      // 可选：关闭Modal
      // const modal = document.querySelector(`#BundleModal-${categoryId}`);
      // if (modal && modal.hasAttribute('open')) {
      //   modal.removeAttribute('open');
      // }

      console.log('[Bundle Builder] Cleared all products in category:', categoryId);
    }

    /**
     * 产品卡片变体变化
     */
    handleProductVariantChange(select) {
      const productCard = select.closest('.bundle-product-card');
      const selectedOption = select.selectedOptions[0];
      const newVariantId = parseInt(selectedOption.value);
      const oldVariantId = parseInt(productCard.dataset.variantId);
      const categoryId = productCard.closest('.bundle-modal').dataset.categoryId;

      // 更新卡片数据
      productCard.dataset.variantId = newVariantId;
      productCard.dataset.price = selectedOption.dataset.price;
      productCard.dataset.comparePrice = selectedOption.dataset.comparePrice || '';
      productCard.dataset.image = selectedOption.dataset.image || productCard.dataset.image;

      // 更新图片
      const img = productCard.querySelector('[data-product-card-image]');
      if (img && selectedOption.dataset.image) {
        img.src = selectedOption.dataset.image;
      }

      // 更新产品链接（跳转到新变体的详情页）
      const productLink = productCard.querySelector('[data-product-link]');
      if (productLink) {
        const productHandle = productCard.dataset.productHandle;
        productLink.href = `/products/${productHandle}?variant=${newVariantId}`;
      }

      // 更新价格显示
      const priceDisplay = productCard.querySelector('[data-product-card-price]');
      if (priceDisplay) {
        const comparePrice = parseFloat(selectedOption.dataset.comparePrice || 0) / 100;
        const currentPrice = parseFloat(selectedOption.dataset.price) / 100;

        const comparePriceEl = priceDisplay.querySelector('[data-compare-price]');
        const currentPriceEl = priceDisplay.querySelector('[data-current-price]');

        if (comparePriceEl && comparePrice > currentPrice) {
          comparePriceEl.innerHTML = this.formatMoney(comparePrice);
          comparePriceEl.style.display = '';
        } else if (comparePriceEl) {
          comparePriceEl.style.display = 'none';
        }

        if (currentPriceEl) {
          currentPriceEl.innerHTML = this.formatMoney(currentPrice);
        }
      }

      // 重要修复：变体切换时，只更新产品卡片的UI状态，不修改已选中的变体
      // 用户可以先选择变体1点击Add+，然后切换到变体2再点击Add+，这样会同时选中两个变体
      // 更新产品卡片的选中状态UI（显示新变体是否已被选中）
      this.updateProductCardState(productCard, categoryId);
    }

    /**
     * 类目卡片数量变化
     */
    handleCategoryQuantityChange(categoryId, newQuantity) {
      const selectedProducts = this.state.collections[categoryId].selectedProducts;

      // 单产品状态下，只有一个产品
      if (selectedProducts.size === 1) {
        const [variantId, product] = Array.from(selectedProducts.entries())[0];
        product.quantity = Math.max(1, newQuantity);

        this.updateCategoryCard(categoryId);
        this.updateCartBadges();
        this.updateAddToCartButton();
      }
    }

    /**
     * 更新产品卡片状态
     */
    updateProductCardState(productCard, categoryId) {
      const variantId = parseInt(productCard.dataset.variantId);
      const selectedProducts = this.state.collections[categoryId].selectedProducts;
      const isSelected = selectedProducts.has(variantId);

      if (isSelected) {
        const product = selectedProducts.get(variantId);
        productCard.dataset.state = 'selected';

        // 显示数量徽章
        const quantityBadge = productCard.querySelector('[data-quantity-badge]');
        if (quantityBadge) {
          quantityBadge.style.display = 'flex';
          quantityBadge.querySelector('[data-quantity-value]').textContent = product.quantity;
        }

        // 显示删除按钮
        const removeBtn = productCard.querySelector('[data-remove-product]');
        if (removeBtn) {
          removeBtn.style.display = 'flex';
        }

        // 显示选中勾
        const indicator = productCard.querySelector('[data-selected-indicator]');
        if (indicator) {
          indicator.style.display = 'flex';
        }
      } else {
        productCard.dataset.state = '';

        // 隐藏数量徽章
        const quantityBadge = productCard.querySelector('[data-quantity-badge]');
        if (quantityBadge) {
          quantityBadge.style.display = 'none';
        }

        // 隐藏删除按钮
        const removeBtn = productCard.querySelector('[data-remove-product]');
        if (removeBtn) {
          removeBtn.style.display = 'none';
        }

        // 隐藏选中勾
        const indicator = productCard.querySelector('[data-selected-indicator]');
        if (indicator) {
          indicator.style.display = 'none';
        }
      }
    }

    /**
     * 更新类目卡片显示
     */
    updateCategoryCard(categoryId) {
      const card = this.querySelector(`[data-category-id="${categoryId}"]`);
      if (!card) return;

      const selectedProducts = this.state.collections[categoryId].selectedProducts;
      const variantCount = selectedProducts.size; // 不同变体的数量

      // 计算总产品数量（包括数量>1的情况）
      let totalQuantity = 0;
      selectedProducts.forEach(product => {
        totalQuantity += product.quantity;
      });

      const emptyState = card.querySelector('[data-category-empty]');
      const singleState = card.querySelector('[data-category-single]');
      const multipleState = card.querySelector('[data-category-multiple]');

      console.log(`[Bundle Builder] updateCategoryCard: ${categoryId}, variants: ${variantCount}, totalQuantity: ${totalQuantity}`);

      if (variantCount === 0) {
        // 空状态
        card.dataset.state = 'empty';
        if (emptyState) emptyState.style.display = 'flex';
        if (singleState) singleState.style.display = 'none';
        if (multipleState) multipleState.style.display = 'none';
      } else if (variantCount === 1 && totalQuantity === 1) {
        // 单产品状态：只有1个变体且数量为1
        card.dataset.state = 'selected-single';
        if (emptyState) emptyState.style.display = 'none';
        if (singleState) singleState.style.display = 'flex';
        if (multipleState) multipleState.style.display = 'none';

        this.renderSingleProduct(card, categoryId);
      } else {
        // 多产品状态：多个变体 OR 单个变体但数量>1
        card.dataset.state = 'selected-multiple';
        if (emptyState) emptyState.style.display = 'none';
        if (singleState) singleState.style.display = 'none';
        if (multipleState) multipleState.style.display = 'flex';

        this.renderMultipleProducts(card, categoryId);
      }

      // 更新对应modal的副标题
      this.updateModalSubtitle(categoryId, variantCount);

      // 更新Modal左上角数量徽章
      this.updateModalCountBadge(categoryId, variantCount);

      // 更新Modal右上角清空按钮
      this.updateModalClearButton(categoryId, variantCount);
    }

    /**
     * 渲染单个产品
     */
    renderSingleProduct(card, categoryId) {
      const selectedProducts = this.state.collections[categoryId].selectedProducts;
      const [variantId, product] = Array.from(selectedProducts.entries())[0];

      const singleState = card.querySelector('[data-category-single]');
      if (!singleState) return;

      // 更新图片
      const imageContainer = singleState.querySelector('[data-product-image]');
      if (imageContainer) {
        imageContainer.innerHTML = `<img src="${product.image}" alt="${product.title}" loading="lazy">`;
      }

      // 更新标题
      const titleEl = singleState.querySelector('[data-product-title]');
      if (titleEl) {
        titleEl.textContent = product.title;
      }

      // 更新价格
      const priceDisplay = singleState.querySelector('[data-product-price]');
      if (priceDisplay) {
        const comparePriceEl = priceDisplay.querySelector('[data-compare-price]');
        const currentPriceEl = priceDisplay.querySelector('[data-current-price]');

        if (comparePriceEl && product.compareAtPrice > product.price) {
          comparePriceEl.innerHTML = this.formatMoney(product.compareAtPrice);
          comparePriceEl.style.display = '';
        } else if (comparePriceEl) {
          comparePriceEl.style.display = 'none';
        }

        if (currentPriceEl) {
          currentPriceEl.innerHTML = this.formatMoney(product.price);
        }
      }

      // 更新数量输入框
      const quantityInput = singleState.querySelector('[data-quantity-input]');
      if (quantityInput) {
        quantityInput.value = product.quantity;

        // 触发主题 quantity-selector 的验证，以便更新 +/- 按钮的禁用状态
        const quantitySelector = quantityInput.closest('quantity-selector');
        if (quantitySelector && quantitySelector.validateQtyRules) {
          quantitySelector.validateQtyRules();
        }
      }
    }

    /**
     * 渲染多个产品（扑克牌）
     */
    renderMultipleProducts(card, categoryId) {
      const selectedProducts = Array.from(this.state.collections[categoryId].selectedProducts.values());
      const variantCount = selectedProducts.length; // 不同变体的数量

      // 计算总产品数量（考虑quantity）
      const totalQuantity = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);

      console.log('[Bundle Builder] Rendering multiple products: variants:', variantCount, 'totalQuantity:', totalQuantity);
      console.log('[Bundle Builder] Products:', selectedProducts.map(p => `${p.title} (Variant: ${p.variantId}, Qty: ${p.quantity})`));

      const multipleState = card.querySelector('[data-category-multiple]');
      if (!multipleState) return;

      // 渲染扑克牌
      const stackContainer = multipleState.querySelector('[data-product-stack]');
      if (stackContainer) {
        const maxDisplay = 3;
        const productsToShow = selectedProducts.slice(0, maxDisplay);

        console.log('[Bundle Builder] Showing', productsToShow.length, 'images in stack');
        let stackHTML = productsToShow.map(product =>
          `<img src="${product.image}" alt="${product.title}" loading="lazy">`
        ).join('');

        if (variantCount > maxDisplay) {
          stackHTML += '<span class="ellipsis">...</span>';
        }

        stackContainer.innerHTML = stackHTML;
        console.log('[Bundle Builder] Stack HTML:', stackHTML);
      }

      // 更新数量显示 - 显示总产品数量
      const countEl = multipleState.querySelector('[data-stack-count]');
      if (countEl) {
        countEl.textContent = totalQuantity;
      }

      // 更新总价
      const totalPrice = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
      const totalComparePrice = selectedProducts.reduce((sum, p) => {
        const basePrice = p.compareAtPrice || p.price;
        return sum + (basePrice * p.quantity);
      }, 0);

      const priceDisplay = multipleState.querySelector('[data-stack-price]');
      if (priceDisplay) {
        const comparePriceEl = priceDisplay.querySelector('[data-stack-compare-price]');
        const currentPriceEl = priceDisplay.querySelector('[data-stack-current-price]');

        if (comparePriceEl && totalComparePrice > totalPrice) {
          comparePriceEl.innerHTML = this.formatMoney(totalComparePrice);
          comparePriceEl.style.display = '';
        } else if (comparePriceEl) {
          comparePriceEl.style.display = 'none';
        }

        if (currentPriceEl) {
          currentPriceEl.innerHTML = this.formatMoney(totalPrice);
        }
      }
    }

    /**
     * 更新Modal副标题
     */
    updateModalSubtitle(categoryId, count) {
      // 查找对应的modal副标题元素(modal可能在body下)
      const subtitle = document.querySelector(`[data-modal-subtitle][data-category-id="${categoryId}"]`);
      if (!subtitle) return;

      // 始终显示固定文字
      subtitle.textContent = 'Add products to build your bundle';
    }

    /**
     * 更新Modal左上角数量徽章
     */
    updateModalCountBadge(categoryId, count) {
      const modal = document.querySelector(`#BundleModal-${categoryId}`);
      if (!modal) return;

      const badge = modal.querySelector('[data-modal-count-badge]');
      if (!badge) return;

      if (count > 0) {
        const countValue = badge.querySelector('[data-count-value]');
        if (countValue) {
          countValue.textContent = count;
        }
        badge.style.display = 'flex';
      } else {
        badge.style.display = 'none';
      }
    }

    /**
     * 更新Modal右上角清空按钮
     */
    updateModalClearButton(categoryId, count) {
      const modal = document.querySelector(`#BundleModal-${categoryId}`);
      if (!modal) return;

      const clearBtn = modal.querySelector('[data-clear-all]');
      if (!clearBtn) return;

      if (count > 0) {
        clearBtn.style.display = 'flex';
      } else {
        clearBtn.style.display = 'none';
      }
    }

    /**
     * 更新购物篮徽章
     */
    updateCartBadges() {
      const totalQuantity = this.calculateTotalQuantity();

      // 主区域的徽章（在组件内）
      this.querySelectorAll('[data-cart-badge-count]').forEach(badge => {
        badge.textContent = totalQuantity;
      });

      // 弹窗中的徽章（modal已被移动到body下，需要用document查找）
      const modals = document.querySelectorAll('.bundle-modal');
      modals.forEach(modal => {
        const categoryId = modal.dataset.categoryId;
        // 检查是否属于当前bundle builder
        if (this.state.collections[categoryId]) {
          const badge = modal.querySelector('[data-cart-badge-count]');
          if (badge) {
            badge.textContent = totalQuantity;
          }
        }
      });
    }

    /**
     * 计算总数量
     */
    calculateTotalQuantity() {
      // Campaign模式下没有主产品，从0开始计算
      const isCampaignMode = this.dataset.campaignMode === 'true';
      let total = isCampaignMode ? 0 : (this.state.mainProduct?.quantity || 0);

      for (const collection of Object.values(this.state.collections)) {
        for (const product of collection.selectedProducts.values()) {
          total += product.quantity;
        }
      }

      return total;
    }

    /**
     * 更新Add Bundle to Cart按钮
     */
    updateAddToCartButton() {
      const button = this.elements.addToCartButton;
      if (!button) return;

      // 检查是否为活动页模式
      const isCampaignMode = this.dataset.campaignMode === 'true';

      // 计算有选中产品的类目数量
      const categoriesWithProducts = Object.values(this.state.collections)
        .filter(c => c.selectedProducts.size > 0).length;

      // 获取桌面端和移动端的价格区域
      const footerLeftDesktop = this.querySelector('[data-bundle-footer-left]');
      const footerLeftMobile = this.querySelector('[data-bundle-footer-left-mobile]');

      let canAddToCart = false;

      if (isCampaignMode) {
        // 活动页模式：需要至少2个类目有选中产品
        canAddToCart = categoriesWithProducts >= 2;
      } else {
        // 普通模式：需要主产品 + 至少1个类目有选中产品
        canAddToCart = categoriesWithProducts > 0;
      }

      if (!canAddToCart) {
        // 未满足添加条件
        button.disabled = true;
        if (footerLeftDesktop) footerLeftDesktop.style.display = 'none';
        if (footerLeftMobile) footerLeftMobile.style.display = 'none';
      } else {
        // 满足添加条件，启用按钮，显示左侧价格区域
        button.disabled = false;
        if (footerLeftDesktop) footerLeftDesktop.style.display = 'flex';
        if (footerLeftMobile) footerLeftMobile.style.display = 'flex';

        const pricing = this.calculateBundlePricing();

        // ========== 更新桌面端价格显示 ==========

        // 更新最终Bundle价格
        const totalPriceEl = this.querySelector('[data-bundle-total-price]');
        if (totalPriceEl) {
          totalPriceEl.innerHTML = this.formatMoney(pricing.bundlePrice);
        }

        // 更新原价总和（划线价）
        const originalPriceEl = this.querySelector('[data-bundle-original-price]');
        if (originalPriceEl) {
          // 只有当原价总和大于最终价格时才显示划线价
          if (pricing.originalTotal > pricing.bundlePrice) {
            originalPriceEl.innerHTML = this.formatMoney(pricing.originalTotal);
            originalPriceEl.style.display = '';
          } else {
            originalPriceEl.style.display = 'none';
          }
        }

        // 更新动态折扣百分比标签（桌面端）
        const discountTagEl = this.querySelector('[data-bundle-discount-tag]');
        if (discountTagEl && pricing.actualSavePercentage > 0) {
          discountTagEl.textContent = `Save ${pricing.actualSavePercentage}%`;
        }

        // ========== 更新移动端价格显示 ==========

        // 更新移动端最终价格
        const totalPriceMobileEl = this.querySelector('[data-bundle-total-price-mobile]');
        if (totalPriceMobileEl) {
          totalPriceMobileEl.innerHTML = this.formatMoney(pricing.bundlePrice);
        }

        // 更新移动端Saved标签（显示百分比）
        const savedTagMobileEl = this.querySelector('[data-bundle-saved-tag-mobile]');
        if (savedTagMobileEl && pricing.actualSavePercentage > 0) {
          savedTagMobileEl.textContent = `Saved ${pricing.actualSavePercentage}%`;
          savedTagMobileEl.style.display = '';
        } else if (savedTagMobileEl) {
          savedTagMobileEl.style.display = 'none';
        }
      }
    }

    /**
     * 计算Bundle价格
     *
     * 逻辑：
     * 1. subtotalSalePrice: 所有产品的实际售价（price）相加
     * 2. subtotalOriginalPrice: 所有产品的原价（compare_at_price，没有则用price）相加
     * 3. 对售价总和应用bundle折扣（如10%）
     * 4. 计算实际节省的百分比（动态）
     */
    calculateBundlePricing() {
      let subtotalSalePrice = 0;      // 售价总和（用于计算bundle价格）
      let subtotalOriginalPrice = 0;  // 原价总和（用于显示划线价）

      // 检查是否为活动页模式
      const isCampaignMode = this.dataset.campaignMode === 'true';

      // 主产品（仅在非活动页模式下计算）
      if (!isCampaignMode && this.state.mainProduct) {
        subtotalSalePrice += this.state.mainProduct.price * this.state.mainProduct.quantity;
        const mainOriginalPrice = this.state.mainProduct.compareAtPrice || this.state.mainProduct.price;
        subtotalOriginalPrice += mainOriginalPrice * this.state.mainProduct.quantity;
      }

      // 类目产品
      for (const collection of Object.values(this.state.collections)) {
        for (const product of collection.selectedProducts.values()) {
          subtotalSalePrice += product.price * product.quantity;
          const productOriginalPrice = product.compareAtPrice || product.price;
          subtotalOriginalPrice += productOriginalPrice * product.quantity;
        }
      }

      // 应用bundle折扣到售价总和
      const bundlePrice = subtotalSalePrice * (1 - this.config.discountPercentage / 100);
      const finalPrice = Math.ceil(bundlePrice * 100) / 100;

      // 计算实际节省的百分比（原价总和 vs 最终价格）
      const actualSavePercentage = subtotalOriginalPrice > 0
        ? Math.round(((subtotalOriginalPrice - finalPrice) / subtotalOriginalPrice) * 100)
        : 0;

      return {
        originalTotal: subtotalOriginalPrice,    // 原价总和
        saleTotal: subtotalSalePrice,            // 售价总和
        bundlePrice: finalPrice,                 // 最终Bundle价格
        discountPercentage: this.config.discountPercentage,  // 配置的折扣百分比
        actualSavePercentage: actualSavePercentage  // 实际节省百分比
      };
    }

    async handleAddBundleToCart() {
      const button = this.elements.addToCartButton;
      const originalText = button.innerHTML;
      button.disabled = true;
      button.innerHTML = '<span class="button-text">Adding...</span>';

      try {
        const items = [];
        const isCampaignMode = this.dataset.campaignMode === 'true';

        if (!isCampaignMode) {
          if (!this.state.mainProduct.variantId) {
            throw new Error('Main product variant not selected');
          }
          items.push({
            id: this.state.mainProduct.variantId,
            quantity: this.state.mainProduct.quantity
          });
        }

        for (const collection of Object.values(this.state.collections)) {
          for (const product of collection.selectedProducts.values()) {
            if (!product.variantId || isNaN(product.variantId)) {
              throw new Error(`Cannot find variant for product: ${product.title || 'Unknown'}`);
            }
            items.push({
              id: product.variantId,
              quantity: product.quantity
            });
          }
        }

        const startTime = Date.now();
        let cartOpened = false;

        for (let i = 0; i < items.length; i++) {
          const item = items[i];

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items: [item] })
          });

          if (!response.ok) {
            let errorMsg = 'Failed to add to cart';
            try { const errorData = await response.json(); errorMsg = errorData.description || errorMsg; } catch (e) {}
            throw new Error(errorMsg);
          }

          if (!cartOpened) {
            const elapsed = Date.now() - startTime;
            if (elapsed < 300) {
              await new Promise(resolve => setTimeout(resolve, 300 - elapsed));
            }

            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

            const cartDrawer = document.getElementById('Cart-Drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }

            button.disabled = false;
            button.innerHTML = originalText;
            cartOpened = true;
          }

          await new Promise(resolve => setTimeout(resolve, 150));
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        this.showToast('Bundle added to cart!', 'success');

      } catch (error) {
        this.showToast('Failed to add bundle: ' + error.message, 'error', 4000);
        button.disabled = false;
        button.innerHTML = originalText;
      }
    }

    /**
     * 更新购物车数量显示
     */
    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const cart = await response.json();
        console.log('[Bundle Builder] Cart data:', cart);

        // 更新所有购物车计数元素
        const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count], .header-cart-count, .cart-item-count');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          console.log('[Bundle Builder] Updated cart count element:', el);
        });
      } catch (error) {
        console.error('[Bundle Builder] Failed to update cart count:', error);
      }
    }

    /**
     * 打开弹窗
     */
    openModal(categoryId) {
      // modal 被 modal-dialog.js 移动到了 body 下，所以使用 document.querySelector
      const modal = document.querySelector(`#BundleModal-${categoryId}`);
      if (!modal) return;

      // 使用主题的modal-opener触发
      const opener = modal.nextElementSibling;
      if (opener && opener.tagName === 'MODAL-OPENER') {
        opener.querySelector('span').click();
      } else {
        // 备用方案
        modal.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
      }

      // 更新弹窗内所有产品卡片状态
      modal.querySelectorAll('.bundle-product-card').forEach(card => {
        this.updateProductCardState(card, categoryId);
      });
    }

    /**
     * 关闭弹窗
     */
    closeModal(categoryId) {
      // modal 被 modal-dialog.js 移动到了 body 下，所以使用 document.querySelector
      const modal = document.querySelector(`#BundleModal-${categoryId}`);
      if (!modal) return;

      modal.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }

    /**
     * 打开管理弹窗
     */
    openManageModal(categoryId) {
      const modal = document.querySelector(`#BundleManageModal-${categoryId}`);
      if (!modal) {
        console.error('[Bundle Builder] Manage modal not found for:', categoryId);
        return;
      }

      // 标记弹窗为未修改状态
      modal.dataset.modified = 'false';

      // 渲染内容
      this.updateManageModalContent(categoryId);

      // 打开弹窗
      modal.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');

      console.log('[Bundle Builder] Manage modal opened:', categoryId);
    }

    /**
     * 关闭管理弹窗（带确认）
     */
    handleManageClose(categoryId) {
      const modal = document.querySelector(`#BundleManageModal-${categoryId}`);
      if (!modal) return;

      // 检查是否有修改
      if (modal.dataset.modified === 'true') {
        // 显示确认对话框
        if (confirm('Save changes before closing?')) {
          this.handleManageConfirm(categoryId);
        } else {
          this.closeManageModal(categoryId);
        }
      } else {
        this.closeManageModal(categoryId);
      }
    }

    /**
     * 关闭管理弹窗（直接关闭）
     */
    closeManageModal(categoryId) {
      const modal = document.querySelector(`#BundleManageModal-${categoryId}`);
      if (!modal) return;

      modal.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');

      console.log('[Bundle Builder] Manage modal closed:', categoryId);
    }

    /**
     * Confirm按钮点击
     */
    handleManageConfirm(categoryId) {
      // 关闭弹窗
      this.closeManageModal(categoryId);

      // 更新主界面
      this.updateCategoryCard(categoryId);
      this.updateCartBadges();
      this.updateAddToCartButton();

      // ✅ 刷新Modal中的产品卡片状态(修复删除产品后Modal仍显示选中的bug)
      this.refreshModalProductStates(categoryId);

      // 显示Toast提示
      this.showToast('Changes saved successfully');

      console.log('[Bundle Builder] Manage modal confirmed:', categoryId);
    }

    /**
     * 刷新Modal中产品卡片的选中状态
     * 修复:管理弹窗删除产品后,Modal中仍显示选中状态的bug
     */
    refreshModalProductStates(categoryId) {
      // 查找对应的Modal
      const modal = document.querySelector(`#BundleModal-${categoryId}`);
      if (!modal) {
        console.log('[Bundle Builder] Modal not found for refreshing:', categoryId);
        return;
      }

      // 获取Modal中所有产品卡片
      const productCards = modal.querySelectorAll('.bundle-product-card');

      // 遍历更新每个产品卡片的状态
      productCards.forEach(card => {
        this.updateProductCardState(card, categoryId);
      });

      console.log('[Bundle Builder] Refreshed', productCards.length, 'product card states in modal:', categoryId);
    }

    /**
     * 渲染管理弹窗内容
     */
    updateManageModalContent(categoryId) {
      const modal = document.querySelector(`#BundleManageModal-${categoryId}`);
      if (!modal) return;

      const selectedProducts = this.state.collections[categoryId].selectedProducts;
      const productsArray = Array.from(selectedProducts.values());
      const count = productsArray.length;

      // 更新标题
      const countEl = modal.querySelector('[data-manage-count]');
      if (countEl) {
        countEl.textContent = count;
      }

      // 更新产品列表
      const body = modal.querySelector('[data-manage-body]');
      if (body) {
        if (count === 0) {
          body.innerHTML = '<p style="text-align:center;color:#999;padding:40px 0;">No products selected</p>';
        } else {
          body.innerHTML = productsArray.map(product => `
            <div class="bundle-manage-product-item" data-variant-id="${product.variantId}">
              <div class="bundle-manage-product-item__image">
                <img src="${product.image}" alt="${product.title}" loading="lazy">
              </div>
              <div class="bundle-manage-product-item__info">
                <h4 class="bundle-manage-product-item__title">${product.title}</h4>
                <div class="bundle-manage-product-item__price">
                  <span class="current-price">${this.formatMoney(product.price)}</span>
                  ${product.compareAtPrice > product.price ? `<s class="compare-price">${this.formatMoney(product.compareAtPrice)}</s>` : ''}
                </div>
              </div>
              <div class="bundle-manage-product-item__variant-selector">
                ${product.variants && product.variants.length > 1 ? `
                  <select
                    class="bundle-manage-variant-select"
                    data-manage-variant-selector
                    data-product-variant-id="${product.variantId}"
                    aria-label="Select variant">
                    ${product.variants.map(v => `
                      <option
                        value="${v.id}"
                        data-price="${v.price}"
                        data-compare-price="${v.compareAtPrice}"
                        data-image="${v.image}"
                        ${v.id === product.variantId ? 'selected' : ''}>
                        ${v.title}
                      </option>
                    `).join('')}
                  </select>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" class="icon-caret">
                    <path d="M12.6667 6L8 10.6667L3.33333 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                ` : ''}
              </div>
              <div class="bundle-manage-product-item__quantity">
                <input type="number" value="${product.quantity}" min="1" aria-label="Quantity">
                <div class="bundle-manage-product-item__quantity-buttons">
                  <button class="plus" type="button" aria-label="Increase">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M5 1v8M1 5h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                  <button class="minus" type="button" aria-label="Decrease" ${product.quantity <= 1 ? 'disabled' : ''}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1 5h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
              </div>
              <button class="bundle-manage-product-item__remove" data-manage-remove aria-label="Remove">×</button>
            </div>
          `).join('');
        }
      }

      // 更新总价
      const totalPrice = productsArray.reduce((sum, p) => sum + (p.price * p.quantity), 0);
      const totalComparePrice = productsArray.reduce((sum, p) => {
        const basePrice = p.compareAtPrice || p.price;
        return sum + (basePrice * p.quantity);
      }, 0);

      const totalPriceEl = modal.querySelector('[data-manage-total-price]');
      if (totalPriceEl) {
        totalPriceEl.innerHTML = this.formatMoney(totalPrice);
      }

      const totalCompareEl = modal.querySelector('[data-manage-total-compare]');
      if (totalCompareEl) {
        if (totalComparePrice > totalPrice) {
          totalCompareEl.innerHTML = this.formatMoney(totalComparePrice);
          totalCompareEl.style.display = '';
        } else {
          totalCompareEl.style.display = 'none';
        }
      }
    }

    /**
     * 管理弹窗中的数量变化
     */
    handleManageQuantityChange(categoryId, variantId, delta) {
      const selectedProducts = this.state.collections[categoryId].selectedProducts;
      const product = selectedProducts.get(variantId);

      if (product) {
        product.quantity = Math.max(1, product.quantity + delta);

        // 立即同步所有UI - 不需要等待Confirm按钮
        this.updateManageModalContent(categoryId);
        this.updateCategoryCard(categoryId);
        this.updateCartBadges();
        this.updateAddToCartButton();

        console.log('[Bundle Builder] Manage quantity changed and UI synced:', variantId, product.quantity);
      }
    }

    /**
     * 管理弹窗中删除产品
     */
    handleManageProductRemove(categoryId, variantId) {
      const selectedProducts = this.state.collections[categoryId].selectedProducts;
      selectedProducts.delete(variantId);

      // 立即同步所有UI - 不需要等待Confirm按钮
      this.updateManageModalContent(categoryId);
      this.updateCategoryCard(categoryId);
      this.updateCartBadges();
      this.updateAddToCartButton();

      // 同步刷新Modal中的产品卡片状态
      this.refreshModalProductStates(categoryId);

      // 🔧 同步更新产品网格中的产品卡片状态（活动页）- 使用全局查找
      // 查找所有匹配的产品卡片（包括在产品网格和Modal中的）
      const allProductCards = document.querySelectorAll(
        `.bundle-product-card[data-variant-id="${variantId}"][data-category-id="${categoryId}"]`
      );

      console.log('[Bundle Builder] Found product cards to sync:', allProductCards.length);

      allProductCards.forEach(card => {
        if (this.updateProductCardState) {
          this.updateProductCardState(card, categoryId);
          console.log('[Bundle Builder] Synced product card after manage modal delete');
        }
      });

      // 🔧 如果是活动页模式，触发自定义事件通知活动页扩展
      if (this.dataset.campaignMode === 'true') {
        console.log('[Bundle Builder] Campaign mode detected, dispatching sync event');
        this.dispatchEvent(new CustomEvent('bundle:manage-delete', {
          bubbles: true,
          detail: { categoryId, variantId }
        }));
      }

      // 如果删除到0个产品，自动关闭弹窗
      if (selectedProducts.size === 0) {
        this.closeManageModal(categoryId);
      }

      console.log('[Bundle Builder] Product removed in manage modal and all UI synced:', variantId);
    }

    /**
     * 管理弹窗中切换产品变体
     */
    handleManageVariantChange(categoryId, oldVariantId, newVariantId, selectedOption) {
      const selectedProducts = this.state.collections[categoryId].selectedProducts;
      const oldProduct = selectedProducts.get(oldVariantId);

      if (!oldProduct) {
        console.error('[Bundle Builder] Old product not found:', oldVariantId);
        return;
      }

      console.log('[Bundle Builder] Changing variant from', oldVariantId, 'to', newVariantId);

      // 从旧变体中提取数据并更新
      const newPrice = parseFloat(selectedOption.dataset.price);
      const newComparePrice = parseFloat(selectedOption.dataset.comparePrice || 0);
      const newImage = selectedOption.dataset.image || oldProduct.image;

      // 如果新变体ID不同于旧变体ID，需要重新创建产品条目
      if (oldVariantId !== newVariantId) {
        // 🔧 创建新的Map，保持原有顺序，只替换变体ID
        const newMap = new Map();
        for (const [variantId, product] of selectedProducts.entries()) {
          if (variantId === oldVariantId) {
            // 遇到旧变体时，用新变体替换（保持原数量和其他信息）
            const newProduct = {
              ...oldProduct,
              variantId: newVariantId,
              price: newPrice,
              compareAtPrice: newComparePrice,
              image: newImage
            };
            newMap.set(newVariantId, newProduct);
            console.log('[Bundle Builder] Variant switched:', {
              old: oldVariantId,
              new: newVariantId,
              product: newProduct
            });
          } else {
            // 其他产品保持不变
            newMap.set(variantId, product);
          }
        }

        // 替换整个Map
        this.state.collections[categoryId].selectedProducts = newMap;

        // 同步更新旧变体的产品卡片状态（取消选中）
        const oldProductCards = document.querySelectorAll(
          `.bundle-product-card[data-variant-id="${oldVariantId}"][data-category-id="${categoryId}"]`
        );
        oldProductCards.forEach(card => {
          if (this.updateProductCardState) {
            this.updateProductCardState(card, categoryId);
          }
        });

        // 同步更新新变体的产品卡片状态（选中）
        const newProductCards = document.querySelectorAll(
          `.bundle-product-card[data-variant-id="${newVariantId}"][data-category-id="${categoryId}"]`
        );
        newProductCards.forEach(card => {
          if (this.updateProductCardState) {
            this.updateProductCardState(card, categoryId);
          }
        });
      } else {
        // 同一个变体，只更新价格和图片
        oldProduct.price = newPrice;
        oldProduct.compareAtPrice = newComparePrice;
        oldProduct.image = newImage;
      }

      // 更新所有UI
      this.updateManageModalContent(categoryId);
      this.updateCategoryCard(categoryId);
      this.updateCartBadges();
      this.updateAddToCartButton();
      this.refreshModalProductStates(categoryId);

      // 🔧 如果是活动页模式，触发自定义事件通知活动页扩展
      if (this.dataset.campaignMode === 'true') {
        this.dispatchEvent(new CustomEvent('bundle:state-changed', {
          bubbles: true,
          detail: { categoryId, variantId: newVariantId, action: 'variant-change' }
        }));
      }

      console.log('[Bundle Builder] Variant change complete, all UI synced');
    }

    /**
     * 显示Toast提示 - 现代化设计
     * @param {string} message - 提示消息
     * @param {string} type - 类型: 'success', 'error', 'info'
     * @param {number} duration - 显示时长(毫秒)
     */
    showToast(message, type = 'success', duration = 3000) {
      // 创建Toast容器
      const toast = document.createElement('div');
      toast.className = `bundle-toast bundle-toast--${type}`;

      // Toast内容结构
      toast.innerHTML = `
        <div class="bundle-toast__icon">
          ${this.getToastIcon(type)}
        </div>
        <div class="bundle-toast__content">
          <p class="bundle-toast__message">${message}</p>
        </div>
        <button type="button" class="bundle-toast__close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      `;

      // 添加到页面
      document.body.appendChild(toast);

      // 触发进入动画
      setTimeout(() => {
        toast.classList.add('bundle-toast--visible');
      }, 10);

      // 关闭按钮事件
      const closeBtn = toast.querySelector('.bundle-toast__close');
      const closeToast = () => {
        toast.classList.remove('bundle-toast--visible');
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      };

      closeBtn.addEventListener('click', closeToast);

      // 自动关闭
      setTimeout(closeToast, duration);
    }

    /**
     * 获取Toast图标SVG
     */
    getToastIcon(type) {
      const icons = {
        success: `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" fill="currentColor" fill-opacity="0.1"/>
            <path d="M6 10L8.5 12.5L14 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `,
        error: `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" fill="currentColor" fill-opacity="0.1"/>
            <path d="M10 6V11M10 14V14.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        `,
        info: `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" fill="currentColor" fill-opacity="0.1"/>
            <path d="M10 10V14M10 7V7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        `
      };
      return icons[type] || icons.info;
    }

    /**
     * 加载更多产品
     */
    async loadMoreProducts(categoryId, button) {
      // modal 被 modal-dialog.js 移动到了 body 下
      const modal = document.querySelector(`#BundleModal-${categoryId}`);
      if (!modal) return;

      const collectionHandle = button.dataset.collectionHandle;
      const currentPage = parseInt(button.dataset.currentPage) || 1;
      const nextPage = currentPage + 1;

      const loading = modal.querySelector('[data-loading]');
      button.style.display = 'none';
      if (loading) loading.style.display = 'block';

      try {
        // 使用Collection页面的分页
        const perPage = this.getPerPageCount();
        const response = await fetch(`/collections/${collectionHandle}?page=${nextPage}`);
        const html = await response.text();

        // 解析HTML并提取产品
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // 这里需要根据实际HTML结构调整选择器
        // 简化版本：假设我们可以通过Ajax API获取产品

        // 更新页面
        button.dataset.currentPage = nextPage;
        button.style.display = 'block';
        if (loading) loading.style.display = 'none';

      } catch (error) {
        console.error('Load more error:', error);
        button.style.display = 'block';
        if (loading) loading.style.display = 'none';
      }
    }

    /**
     * 获取每页产品数量（根据设备）
     */
    getPerPageCount() {
      const width = window.innerWidth;
      if (width >= 1024) return this.config.productsPerPage.desktop;
      if (width >= 768) return this.config.productsPerPage.tablet;
      return this.config.productsPerPage.mobile;
    }

    /**
     * 显示通知
     */
    showNotification(message, type = 'info') {
      // 简单实现，可以替换为主题的通知系统
      console.log(`[${type.toUpperCase()}] ${message}`);

      // 可以添加更好的UI通知
      // 例如：使用主题的toast通知或创建自定义通知元素
    }

    /**
     * 格式化金额 - 使用Shopify商城的货币格式
     * 返回带有 transcy-money class 的 HTML，兼容 Transcy 货币转换应用
     * @param {number} amount - 金额（单位：元，如 29.99）
     * @returns {string} 格式化后的货币 HTML 字符串
     */
    formatMoney(amount) {
      // 使用Shopify的money_format
      // 格式示例：
      // - "${{amount}}" → $29.99
      // - "€{{amount}}" → €29.99
      // - "{{amount_with_comma_separator}} €" → 29,99 €
      // - "{{amount}} kr" → 29.99 kr

      const moneyFormat = this.config.moneyFormat;
      const formattedAmount = amount.toFixed(2);
      let priceText;

      // 替换 {{amount}} 占位符
      if (moneyFormat.includes('{{amount}}')) {
        priceText = moneyFormat.replace('{{amount}}', formattedAmount);
      }
      // 如果格式中包含 {{amount_with_comma_separator}}
      else if (moneyFormat.includes('{{amount_with_comma_separator}}')) {
        // 格式化为欧洲格式：点作为千位分隔符，逗号作为小数分隔符
        const parts = formattedAmount.split('.');
        const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const withComma = intPart + ',' + parts[1];
        priceText = moneyFormat.replace('{{amount_with_comma_separator}}', withComma);
      }
      // 如果格式中包含 {{amount_no_decimals}}
      else if (moneyFormat.includes('{{amount_no_decimals}}')) {
        const noDecimals = Math.round(amount).toString();
        priceText = moneyFormat.replace('{{amount_no_decimals}}', noDecimals);
      }
      // 默认回退：使用全局 formatMoney 函数或 theme 设置
      else {
        if (typeof formatMoney === 'function' && window.theme?.settings?.money_with_currency_format) {
          // 使用全局 formatMoney 函数（金额需要转换为分）
          priceText = formatMoney(amount * 100, window.theme.settings.money_with_currency_format);
        } else {
          // 最终回退：使用店铺货币符号
          priceText = formattedAmount;
        }
      }

      // 返回带有 transcy-money class 的 span，兼容 Transcy 货币转换应用
      return `<span class="transcy-money">${priceText}</span>`;
    }

    /**
     * 更新所有UI
     */
    updateUI() {
      this.updateMainProductPrice();
      this.updateCartBadges();
      this.updateAddToCartButton();

      // 验证主产品数量选择器的按钮状态
      if (this.elements.mainQuantityInput) {
        const quantitySelector = this.elements.mainQuantityInput.closest('quantity-selector');
        if (quantitySelector && quantitySelector.validateQtyRules) {
          quantitySelector.validateQtyRules();
        }
      }

      // 更新所有类目卡片
      ['collection_1', 'collection_2', 'collection_3'].forEach(categoryId => {
        this.updateCategoryCard(categoryId);
      });
    }
  }

  customElements.define('product-bundle-builder', ProductBundleBuilder);
}
